import { Button, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { Toggle } from "../../../components/Toggle/Toggle.js";
import { researchJobsService } from "../../../api/researchJobs.service.js";
import {
  RESEARCH_JOB_SWITCHES,
  canStart,
  initialTicked,
  selectAllState,
  toggleAll,
  type PublicationOption,
  willRunText,
  describeLastFinished,
  describeRunning,
  publicationOptions,
  researchJobButtonState,
  startBody,
  type ResearchJobKey,
  type ResearchJobSummary,
} from "./researchJobState.js";
import styles from "./ResearchDataImport.module.css";

const POLL_INTERVAL_MS = 10000;
const LOAD_ERROR_REASON = "Couldn't load the research jobs' status.";

const JOBS: {
  job: ResearchJobKey;
  buttonLabel: string;
  cardTitle: string;
  title: string;
  description: string;
}[] = [
  {
    job: "daily",
    cardTitle: "Nightly research sync",
    buttonLabel: "Run nightly research sync now",
    title: "Run nightly research sync now",
    description:
      "Starts the nightly research import now, outside its schedule. It still skips any publication whose switch is off.",
  },
  {
    job: "old-editions",
    cardTitle: "Old-editions research sync",
    buttonLabel: "Run old-editions research sync",
    title: "Run old-editions research sync",
    description: "Starts the old-editions research import for archived editions.",
  },
];

const formatTimestamp = (ms: number): string => new Date(ms).toLocaleString();

const placeholderSummary = (job: ResearchJobKey): ResearchJobSummary => ({
  job,
  available: false,
  running: null,
  lastFinished: null,
  publications: null,
});

function SelectAllCheckbox({
  ticked,
  options,
  onToggle,
}: {
  ticked: string[];
  options: PublicationOption[];
  onToggle: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const state = selectAllState(ticked, options);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <label className={styles.checkboxRow}>
      <input
        ref={ref}
        type="checkbox"
        checked={state === "all"}
        disabled={!options.some(option => option.tickable)}
        onChange={onToggle}
      />
      <span>Select all</span>
    </label>
  );
}

export default function ResearchJobButtons() {
  const [switches, setSwitches] = useState<Record<string, boolean>>({});
  const [summaries, setSummaries] = useState<ResearchJobSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dryRunByJob, setDryRunByJob] = useState<Partial<Record<ResearchJobKey, boolean>>>({});
  const [confirmJob, setConfirmJob] = useState<ResearchJobKey | null>(null);
  const [tickedByJob, setTickedByJob] = useState<
    Partial<Record<ResearchJobKey, { signature: string; ticked: string[] }>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await researchJobsService.list();
      setSummaries(response.jobs ?? []);
      setSwitches(response.switches ?? {});
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : LOAD_ERROR_REASON);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const summaryFor = (job: ResearchJobKey): ResearchJobSummary =>
    summaries.find(summary => summary.job === job) ?? placeholderSummary(job);

  const optionsFor = (job: ResearchJobKey): PublicationOption[] =>
    publicationOptions(summaryFor(job), switches);

  const signatureOf = (options: PublicationOption[]): string => initialTicked(options).join(",");

  const tickedFor = (job: ResearchJobKey): string[] => {
    const options = optionsFor(job);
    const stored = tickedByJob[job];
    return stored && stored.signature === signatureOf(options)
      ? stored.ticked
      : initialTicked(options);
  };

  const setTicked = (job: ResearchJobKey, ticked: string[]) =>
    setTickedByJob(current => ({
      ...current,
      [job]: { signature: signatureOf(optionsFor(job)), ticked },
    }));

  const openConfirm = (job: ResearchJobKey) => setConfirmJob(job);

  const closeConfirm = () => {
    if (!submitting) setConfirmJob(null);
  };

  const setDryRun = (job: ResearchJobKey, next: boolean) =>
    setDryRunByJob(current => ({ ...current, [job]: next }));

  const toggleAllFor = (job: ResearchJobKey, options: PublicationOption[]) =>
    setTicked(job, toggleAll(tickedFor(job), options));

  const toggle = (job: ResearchJobKey, slug: string) => {
    const ticked = tickedFor(job);
    setTicked(
      job,
      ticked.includes(slug) ? ticked.filter(item => item !== slug) : [...ticked, slug],
    );
  };

  const confirmStart = async () => {
    if (!confirmJob) return;
    setSubmitting(true);
    try {
      await researchJobsService.start(
        confirmJob,
        startBody(
          tickedFor(confirmJob),
          publicationOptions(summaryFor(confirmJob), switches),
          dryRunByJob[confirmJob] === true,
        ),
      );
      setToast({ message: "Run started.", type: "success" });
    } catch (err) {
      const status = (err as { status?: number }).status;
      setToast(
        status === 409
          ? { message: "A run of this job is already going.", type: "error" }
          : { message: err instanceof Error ? err.message : String(err), type: "error" },
      );
    } finally {
      setSubmitting(false);
      setConfirmJob(null);
      void refresh();
    }
  };

  const confirmConfig = JOBS.find(item => item.job === confirmJob) ?? null;
  const options = confirmJob ? publicationOptions(summaryFor(confirmJob), switches) : [];

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>Research jobs</h2>
      {loadError && <div className={`${styles.notification} ${styles.error}`}>{loadError}</div>}

      <div className={styles.jobGrid}>
        {JOBS.map(({ job, buttonLabel, cardTitle }) => {
          const summary = summaryFor(job);
          const jobOptions = publicationOptions(summary, switches);
          const jobTicked = tickedFor(job);
          const baseState = loadError
            ? { disabled: true, reason: LOAD_ERROR_REASON }
            : researchJobButtonState(summary, switches[RESEARCH_JOB_SWITCHES[job]] === true);
          const state =
            !baseState.disabled && !canStart(jobTicked)
              ? { disabled: true, reason: "Tick at least one publication to run this job." }
              : baseState;
          const status =
            describeRunning(summary.running, formatTimestamp) ??
            describeLastFinished(summary.lastFinished, formatTimestamp);
          return (
            <div key={job} className={styles.jobCard}>
              <h3 className={styles.jobTitle}>{cardTitle}</h3>
              <div className={styles.publicationGroup}>
                <div className={styles.publicationHeader}>
                  <span className={styles.publicationCaption}>Publications</span>
                  <SelectAllCheckbox
                    ticked={jobTicked}
                    options={jobOptions}
                    onToggle={() => toggleAllFor(job, jobOptions)}
                  />
                </div>
                <div className={styles.publicationList}>
                  {jobOptions.map(option => (
                    <label key={option.slug} className={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={jobTicked.includes(option.slug)}
                        disabled={!option.tickable}
                        onChange={() => toggle(job, option.slug)}
                      />
                      <span>
                        {option.label}
                        {option.note ? ` (${option.note})` : ""}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.toggleRow}>
                <Toggle
                  checked={dryRunByJob[job] === true}
                  onChange={next => setDryRun(job, next)}
                  ariaLabel="Dry run"
                />
                <span className={styles.publicationCaption}>Dry run</span>
              </div>
              <div className={styles.formAction}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => openConfirm(job)}
                  disabled={state.disabled}
                >
                  {buttonLabel}
                </Button>
              </div>
              <div className={styles.jobLines}>
                {state.reason && <p className={styles.sectionDescription}>{state.reason}</p>}
                {status && <p className={styles.sectionDescription}>{status}</p>}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={confirmJob !== null}
        onClose={closeConfirm}
        title={confirmConfig?.title ?? ""}
        size="small"
        blurBackground
      >
        <div className={styles.modalBody}>
          <p>{confirmConfig?.description}</p>
          <p>{confirmJob ? willRunText(tickedFor(confirmJob), options) : null}</p>
          {confirmJob && dryRunByJob[confirmJob] === true && (
            <p>Dry run: nothing will be written</p>
          )}
          <div className={styles.modalActions}>
            <Button color="default" variant="outlined" onClick={closeConfirm} disabled={submitting}>
              Cancel
            </Button>
            <Button color="primary" onClick={confirmStart} disabled={submitting}>
              Start run
            </Button>
          </div>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </section>
  );
}
